/**
 * Specialist Workspace Shell — DIGEMP-SPECIALIST-WORKSPACE-SHELL-FRONTEND-052.
 *
 * One reusable operator surface for the specialist domains (ESET / Acronis / M365+Entra, and any
 * future pack added to SPECIALIST_DOMAINS): common navigation (domain tabs), evidence-based
 * readiness, recent domain work, known limits and the escalation / human-control boundary.
 *
 * Every fact on this screen is derived from data the operator can already read elsewhere:
 *   - employees + declared specialties: GET /api/agents/
 *   - observed connections + last connection-test evidence: GET /api/agents/:id (connections)
 *   - recent work: GET /api/agent-runs/ (bounded scan; only bounded scalar summaries are shown,
 *     never run.context, prompts or tool payloads)
 *   - connector catalogue: GET /api/agents/connector-types (agents_manage only — without it the
 *     limits section degrades to an explicit note, never to fabricated limits)
 *   - the domain action map: GET /api/agents/specialists/:domain (agents_view), which is the one
 *     payload that keeps executable reads, executable approval-gated actions, configured-but-
 *     unregistered tools and never-executable escalation material visibly distinct
 * Nothing here grants, approves or executes anything. A connection counts as verified only when
 * its last test says so AND carries a fresh, credible timestamp — stale, untimestamped, failed
 * and future-skewed evidence is shown as such and never counted as verified. The status-token
 * vocabulary here is the same one the vendor specialist pages and the Guardian center use.
 */
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import api, { agentsApi, agentRunsApi, agentConnectionsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useAuth } from "../context/AuthContext";
import { parseUtc } from "../utils/utcDate";

// ─── Domain configuration (the reusable part) ─────────────────────────────────
//
// A new specialist pack is a new entry here plus a connectors.ts-style note — the shell below is
// driven entirely by this list, not by per-vendor code paths.

export const SPECIALIST_DOMAINS = [
  {
    key: "eset",
    label: "ESET",
    title: "מומחה ESET",
    systemTypes: ["eset_protect"],
    connectorId: "eset_protect",
    backendKey: "eset",
  },
  {
    key: "acronis",
    label: "Acronis",
    title: "מומחה Acronis",
    systemTypes: ["acronis_backup"],
    connectorId: "acronis_backup",
    backendKey: "acronis",
  },
  {
    key: "m365",
    label: "Microsoft 365 / Entra",
    title: "מומחה Microsoft 365 / Entra",
    systemTypes: ["m365_graph"],
    connectorId: "m365_graph",
    backendKey: "m365_entra",
  },
];

export function domainForParam(param) {
  return SPECIALIST_DOMAINS.find((d) => d.key === param) || null;
}

// ─── Evidence semantics ───────────────────────────────────────────────────────

// Mirrors backup_guardian's default max evidence age: an "ok" test older than this is not fresh.
export const EVIDENCE_MAX_AGE_HOURS = 25;
const FUTURE_SKEW_MS = 5 * 60 * 1000;

const OK_TOKENS = new Set(["ok", "success"]);
// A status token is a failure only when one of its words is an explicit failure word
// (auth_error → failed); anything else non-empty stays unknown rather than being guessed.
const FAILURE_TOKEN_WORDS = new Set([
  "fail", "failed", "failure", "error", "denied", "invalid", "unauthorized", "rejected",
]);

export function isFailureStatusToken(raw) {
  return String(raw)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((word) => FAILURE_TOKEN_WORDS.has(word));
}

export const EVIDENCE_META = {
  ok:                { label: "אימות תקין",             cls: "bg-green-100 text-green-700 border-green-200", verified: true },
  stale:             { label: "הראיה ישנה",             cls: "bg-amber-100 text-amber-800 border-amber-200", verified: false },
  "ok-no-timestamp": { label: "אימות ללא חתימת זמן",     cls: "bg-amber-100 text-amber-800 border-amber-200", verified: false },
  failed:            { label: "הבדיקה נכשלה",            cls: "bg-red-100 text-red-700 border-red-200",       verified: false },
  never:             { label: "טרם נבדק",                cls: "bg-gray-100 text-gray-600 border-gray-200",    verified: false },
  unknown:           { label: "מצב בדיקה לא מזוהה",      cls: "bg-amber-100 text-amber-800 border-amber-200", verified: false },
  unreliable:        { label: "הראיה אינה אמינה",        cls: "bg-red-100 text-red-700 border-red-200",       verified: false },
};

export function connectionEvidence(connection, now = Date.now()) {
  const rawStatus = typeof connection?.last_test_status === "string" ? connection.last_test_status.trim() : "";
  const token = rawStatus.toLowerCase();
  const testedAt = connection?.last_tested_at || null;
  const testedParsed = testedAt ? parseUtc(testedAt) : null;
  const testedMs = testedParsed ? testedParsed.getTime() : NaN;
  const hasTimestamp = Number.isFinite(testedMs);
  const ageHours = hasTimestamp ? (now - testedMs) / 3_600_000 : null;

  if (!token) return { state: "never", raw: "", ageHours: null, testedAt: null };
  if (OK_TOKENS.has(token)) {
    if (!hasTimestamp) return { state: "ok-no-timestamp", raw: rawStatus, ageHours: null, testedAt: null };
    // A timestamp in the future (beyond clock-skew tolerance) means a reported verification cannot
    // be trusted, no matter what the status token claims — fail closed instead of counting it fresh.
    if (testedMs - now > FUTURE_SKEW_MS) return { state: "unreliable", raw: rawStatus, ageHours: null, testedAt };
    if (ageHours > EVIDENCE_MAX_AGE_HOURS) return { state: "stale", raw: rawStatus, ageHours, testedAt };
    return { state: "ok", raw: rawStatus, ageHours, testedAt };
  }
  if (isFailureStatusToken(rawStatus)) {
    return { state: "failed", raw: rawStatus, ageHours, testedAt };
  }
  // Unknown non-empty token: pass it through truthfully, never map it to a success or a failure.
  return { state: "unknown", raw: rawStatus, ageHours, testedAt };
}

export function isVerifiedEvidence(connection, now = Date.now()) {
  return EVIDENCE_META[connectionEvidence(connection, now).state].verified === true;
}

export function summarizeReadiness(members, now = Date.now()) {
  let total = 0;
  let verified = 0;
  for (const member of members) {
    for (const connection of member.connections) {
      total += 1;
      if (isVerifiedEvidence(connection, now)) verified += 1;
    }
  }
  const state = total === 0 ? "none" : verified > 0 ? "ready" : "attention";
  return { state, total, verified, attention: total - verified };
}

const READINESS_ROLLUP = {
  ready:     { label: "מוכן — לפחות חיבור אחד מאומת ועדכני", cls: "bg-green-50 border-green-200 text-green-800" },
  attention: { label: "נדרש טיפול — אין חיבור מאומת ועדכני", cls: "bg-amber-50 border-amber-200 text-amber-800" },
  none:      { label: "לא נמצא חיבור מוגדר בתחום זה",         cls: "bg-gray-50 border-gray-200 text-gray-600" },
  unknown:   {
    label: "מצב המוכנות אינו ידוע — נתוני החיבורים של חלק מהעובדים טרם נטענו; אין להסיק שאין חיבור מוגדר.",
    cls: "bg-gray-50 border-dashed border-gray-400 text-gray-700",
  },
};

// Absence may only be asserted after every employee's connection details actually loaded: while
// details are pending or failed, an empty member list is unknown, never "none" — and a rollup
// verdict then covers only the connections that did load.
export function domainReadiness(view, now = Date.now()) {
  const readiness = summarizeReadiness(view.members, now);
  const unloaded = view.pending.length + view.failed.length;
  const state = readiness.state === "none" && unloaded > 0 ? "unknown" : readiness.state;
  return { ...readiness, unloaded, state };
}

const READINESS_TAB_TEXT = {
  ready: "מוכן",
  attention: "נדרש טיפול",
  none: "אין חיבור מוגדר",
  unknown: "מצב לא ידוע",
};

// ─── Domain view: employees of a domain, from the observed connections ────────

export function buildDomainView(agents, detailStates, domain) {
  const types = new Set(domain.systemTypes);
  const members = [];
  const failed = [];
  const pending = [];
  agents.forEach((agent, i) => {
    const state = detailStates[i] || {};
    if (state.isError) { failed.push(agent); return; }
    if (!state.data) { pending.push(agent); return; }
    const connections = (Array.isArray(state.data.connections) ? state.data.connections : [])
      .filter((c) => types.has(c.system_type));
    if (connections.length > 0) members.push({ agent, connections });
  });
  return { members, failed, pending };
}

// Runs arrive already ordered (created_at desc) from the API; the scan bound is disclosed in the
// UI so a truncated view can never read as "this is everything the employee ever did".
export const RECENT_RUNS_SCAN = 100;
export const RECENT_RUNS_SHOWN = 20;

export function domainRuns(runs, agentIds, limit = RECENT_RUNS_SHOWN) {
  const ids = new Set(agentIds);
  const matched = (Array.isArray(runs) ? runs : []).filter((r) => ids.has(r.agent_id));
  return { matched: matched.length, shown: matched.slice(0, limit) };
}

// ─── Known limits + escalation, derived from the connector registry ───────────

export function limitsStatements(definition) {
  if (!definition) return [];
  const out = [];
  if (definition.read_only === true) {
    out.push("קריאה בלבד — המחבר אינו מאפשר כתיבה, שינוי או מחיקה.");
  } else if (definition.read_only === false) {
    out.push("המחבר כולל פעולות שאינן קריאה בלבד — כל פעולה טעונה אישור לפי מדיניות.");
  }
  if (definition.implemented === false) {
    out.push("המחבר טרם הוטמע במלואו — היכולות המוצהרות מטה טרם אומתו וטרם נרשמו ככלים.");
  }
  if (definition.target_selection === "device") {
    out.push("נדרשת בחירת מכשיר מפורשת לפני כל פעולה — אין גישה גורפת לכלל המכשירים.");
  }
  if (definition.scope === "customer") {
    out.push("הגישה מוגבלת ללקוח המשויך לחיבור — אין מעבר בין לקוחות.");
  }
  if (definition.supports_test === true) {
    out.push("ניתן לאמת את החיבור בבדיקת חיבור יזומה מתוך ניהול החיבורים.");
  }
  return out;
}

export function escalationStatements(definition, readiness) {
  const out = [];
  if (definition?.read_only === true) {
    out.push("המחבר מוגדר קריאה בלבד — צורך בפעולת כתיבה מוסלם להחלטת טכנאי או מנהל מחוץ למסלול זה.");
  }
  if (definition?.implemented === false) {
    out.push("יכולות שטרם הוטמעו אינן ניתנות להרצה — בקשות כאלה מוסלמות לטיפול אנושי.");
  }
  if (readiness?.state === "none") {
    out.push("לא הוגדר חיבור לתחום — יש להסלים להקמת חיבור לפני הסתמכות על התחום.");
  } else if (readiness && readiness.attention > 0) {
    out.push("קיימים חיבורים שאינם מאומתים — יש להסלים לבדיקת חיבור חוזרת (דורש הרשאת ניהול חיבורים).");
  }
  return out;
}

const CAPABILITY_HE = {
  device_security_status: "מצב אבטחת מכשיר",
  threat_detections: "איתור איומים",
  directory_security_read: "קריאת אבטחת ספרייה",
  user_security_inspection: "סקירת אבטחת משתמשים",
  backup_status: "מצב גיבוי",
  backup_alerts: "התראות גיבוי",
  protected_devices: "מכשירים מוגנים",
  device_lookup: "איתור מכשיר",
  attended_session: "סשן בהשגחה",
  endpoint_diagnostics: "אבחון נקודת קצה",
  log_collection: "איסוף לוגים",
};

export function capabilityLabel(token) {
  return CAPABILITY_HE[token] || null;
}

/**
 * The domain action map from GET /api/agents/specialists/:domain
 * (DIGEMP-SPECIALIST-WORKSPACE-SHELL-BACKEND-052): the one payload that keeps executable reads,
 * executable approval-gated actions, configured-but-unregistered tools and never-executable
 * escalation material distinct. Returns null while the payload is unavailable — unknown, never
 * "none". A read-only executable tool with an approval flag keeps that flag (it is displayed,
 * not silently promoted to an ungated action).
 */
export function deriveDomainActionDepth(payload) {
  if (!payload || typeof payload !== "object") return null;
  const tools = Array.isArray(payload?.capabilities?.executable_tools)
    ? payload.capabilities.executable_tools
    : [];
  const unavailableConfigured = Array.isArray(payload?.capabilities?.unavailable_configured_tools)
    ? payload.capabilities.unavailable_configured_tools
    : [];
  const beyond = payload?.escalation?.beyond_read ?? null;
  return {
    reads: tools.filter((tool) => tool?.operation_type === "read_only"),
    gated: tools.filter((tool) => tool?.operation_type !== "read_only"),
    unavailableConfigured,
    proposedWrites: Array.isArray(beyond?.proposed_write_contracts)
      ? beyond.proposed_write_contracts
      : [],
    blockedCount: Number.isFinite(beyond?.blocked_operations) ? beyond.blocked_operations : 0,
    beyondNote: bounded(beyond?.note, 300),
  };
}

const UNCONFIGURED_REASON = {
  not_in_tool_registry: "מוגדרת לתחום זה אך אינה רשומה במרשם הכלים — אינה ניתנת להרצה.",
};

// ─── Run statuses (labels mirror AgentRunsDashboardPage) ─────────────────────

const RUN_STATUS_LABELS = {
  pending:                         "מאתחל",
  analyzing:                       "מנתח",
  awaiting_technician_review:      "ממתין לסקירה",
  awaiting_approval:               "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור",
  connection_active:               "חיבור פעיל",
  waiting_for_customer_answer:     "ממתין לתשובה",
  diagnosing:                      "מאבחן",
  paused:                          "הופסק",
  resolved:                        "נפתרה",
  completed:                       "הושלם",
  approved:                        "מאושר",
  rejected:                        "נדחה",
  executing:                       "מבצע",
  failed:                          "נכשל",
};

const RUN_STATUS_BADGE = {
  awaiting_technician_review:      "bg-amber-100 text-amber-800 border-amber-200",
  awaiting_approval:               "bg-amber-100 text-amber-800 border-amber-200",
  paused:                          "bg-gray-100 text-gray-700 border-gray-200",
  resolved:                        "bg-green-100 text-green-700 border-green-200",
  completed:                       "bg-green-100 text-green-700 border-green-200",
  failed:                          "bg-red-100 text-red-700 border-red-200",
  rejected:                        "bg-red-100 text-red-700 border-red-200",
};

export function runStatusLabel(status) {
  return RUN_STATUS_LABELS[status] || "מצב לא מזוהה";
}

// Statuses where the run is parked waiting for a human decision (review or action approval).
export const HUMAN_DECISION_STATUSES = new Set(["awaiting_technician_review", "awaiting_approval"]);

export function escalationRuns(runs, agentIds) {
  const ids = new Set(agentIds);
  return (Array.isArray(runs) ? runs : [])
    .filter((r) => ids.has(r.agent_id) && HUMAN_DECISION_STATUSES.has(r.status));
}

// ─── Formatting helpers ───────────────────────────────────────────────────────

function fmtDateTime(iso) {
  if (!iso) return "";
  const parsed = parseUtc(iso);
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  try { return parsed.toLocaleString("he-IL"); } catch { return ""; }
}

function fmtAge(hours) {
  if (hours == null) return "";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} דקות`;
  if (hours < 24) return `${hours.toFixed(1)} שעות`;
  return `${(hours / 24).toFixed(1)} ימים`;
}

function bounded(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

export function evidenceAgeText(evidence) {
  if (evidence.state === "unreliable") return "זמן הבדיקה אינו אמין";
  if (evidence.ageHours != null) return `נבדק לפני ${fmtAge(evidence.ageHours)}`;
  if (evidence.state === "never") return "אין חתימת זמן";
  return "זמן הבדיקה אינו ידוע";
}

function ShellCard({ title, count, children }) {
  return (
    <section aria-label={title} className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {count != null && <span className="text-xs text-gray-400">{count}</span>}
      </div>
      {children}
    </section>
  );
}

// retryLabel keeps every retry in the shell distinguishable for screen readers — several can be
// on screen at once, so the visible "נסה שוב" alone would be ambiguous.
function RetryNote({ message, onRetry, retryLabel }) {
  return (
    <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
      {message}
      <button type="button" onClick={onRetry} aria-label={retryLabel} className="mr-2 underline">נסה שוב</button>
    </p>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function DomainTabs({ activeDomain, readinessByDomain }) {
  return (
    <nav aria-label="תחומי מומחיות" className="flex flex-wrap gap-2">
      {SPECIALIST_DOMAINS.map((d) => {
        const active = d.key === activeDomain.key;
        const readiness = readinessByDomain[d.key];
        // An unloaded/unknown readiness is a hollow ring, never the filled gray of a verified
        // "no connection" — shape carries the distinction, not only color.
        const dot = readiness?.state === "ready"
          ? "bg-green-500"
          : readiness?.state === "attention"
            ? "bg-amber-500"
            : readiness?.state === "none"
              ? "bg-gray-300"
              : "bg-white border border-gray-400";
        return (
          <Link
            key={d.key}
            to={`/specialists/${d.key}`}
            aria-current={active ? "page" : undefined}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
              active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {readiness && <span aria-hidden="true" className={`w-2 h-2 rounded-full ${dot}`} />}
            {d.label}
            {readiness && (
              <span className="sr-only"> — {READINESS_TAB_TEXT[readiness.state] || READINESS_TAB_TEXT.unknown}</span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

function ReadinessSection({ domain, view, now, onRetry }) {
  const readiness = domainReadiness(view, now);
  const rollup = READINESS_ROLLUP[readiness.state];
  const rollupLabel = readiness.state === "attention" && readiness.unloaded > 0
    ? "נדרש טיפול — בין החיבורים שנטענו אין חיבור מאומת ועדכני"
    : rollup.label;
  return (
    <ShellCard title="מוכנות התחום" count={readiness.total > 0 ? `${readiness.total} חיבורים` : null}>
      <div className={`text-sm rounded-lg border px-3 py-2 ${rollup.cls}`}>{rollupLabel}</div>
      {readiness.total > 0 && (
        <p className="text-xs text-gray-500">
          {readiness.verified} מאומתים ועדכניים · {readiness.attention} דורשים טיפול
        </p>
      )}
      <p className="text-[11px] text-gray-400">
        מאומת = בדיקת חיבור אחרונה תקינה ובת פחות מ-{EVIDENCE_MAX_AGE_HOURS} שעות; חתימת זמן עתידית נחשבת לא אמינה.
      </p>

      {view.pending.length > 0 && (
        <p className="text-sm text-gray-400 italic">טוען נתוני חיבורים עבור {view.pending.length} עובדים...</p>
      )}

      {view.failed.length > 0 && (
        <div className="space-y-1">
          {view.failed.map((agent) => (
            <div key={agent.id} className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center justify-between gap-2">
              <span>לא ניתן לטעון את החיבורים של {agent.name}</span>
              <button
                type="button"
                onClick={onRetry}
                aria-label={`נסה שוב לטעינת החיבורים של ${agent.name}`}
                className="underline whitespace-nowrap"
              >
                נסה שוב
              </button>
            </div>
          ))}
        </div>
      )}

      {view.members.length === 0 && view.failed.length === 0 && view.pending.length === 0 && (
        <p className="text-sm text-gray-400 italic">
          לא נמצא עובד עם חיבור {domain.label} מוגדר — לא ניתן להציג מוכנות לתחום זה.
        </p>
      )}

      <ul className="space-y-2">
        {view.members.map(({ agent, connections }) => (
          <li key={agent.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/agents/${agent.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                {agent.name}
              </Link>
              {agent.role_title && <span className="text-xs text-gray-500">{agent.role_title}</span>}
              {agent.is_enabled === false && (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">מושבת</span>
              )}
              {Array.isArray(agent.specialties) && agent.specialties.length > 0 && (
                <span className="text-[11px] text-gray-400">
                  מומחיות מוצהרת: {agent.specialties.map((s) => bounded(s, 40)).filter(Boolean).slice(0, 4).join(", ")}
                </span>
              )}
            </div>
            <ul className="space-y-1">
              {connections.map((c) => {
                const evidence = connectionEvidence(c, now);
                const meta = EVIDENCE_META[evidence.state];
                return (
                  <li key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-gray-700">{bounded(c.display_name, 80) || "חיבור ללא שם תצוגה"}</span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${meta.cls}`}>
                      {meta.label}
                    </span>
                    {evidence.raw && evidence.state !== "ok" && (
                      <span className="text-[11px] text-gray-400" dir="ltr">{bounded(evidence.raw, 40)}</span>
                    )}
                    <span className="text-[11px] text-gray-400 mr-auto whitespace-nowrap" title={fmtDateTime(evidence.testedAt) || undefined}>
                      {evidenceAgeText(evidence)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </ShellCard>
  );
}

function ActionToolRow({ name, description, meta, chips }) {
  const label = bounded(name, 60);
  if (!label) return null;
  return (
    <li className="rounded border border-gray-100 bg-white/70 px-2.5 py-1.5 space-y-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800 font-mono" dir="ltr">{label}</span>
        {chips}
      </div>
      {description && (
        <p className="text-[11px] text-gray-500" dir="ltr">{bounded(description, 160)}</p>
      )}
      {meta && <p className="text-[11px] text-gray-400" dir="ltr">{meta}</p>}
    </li>
  );
}

function ApprovalChips({ entry }) {
  return (
    <>
      {entry?.strong_approval_required ? (
        <span className="text-[11px] px-2 py-0.5 rounded-full border bg-purple-50 border-purple-200 text-purple-700 whitespace-nowrap">
          דורש אישור מוגבר
        </span>
      ) : entry?.approval_required ? (
        <span className="text-[11px] px-2 py-0.5 rounded-full border bg-orange-50 border-orange-200 text-orange-700 whitespace-nowrap">
          דורש אישור אנושי
        </span>
      ) : null}
    </>
  );
}

function ActionDepthSection({ payload, isLoading, isError, onRetry }) {
  const depth = useMemo(() => deriveDomainActionDepth(payload), [payload]);
  const unavailableCount = depth
    ? depth.unavailableConfigured.length + depth.proposedWrites.length
    : 0;
  return (
    <ShellCard title="מפת פעולות התחום" count={depth ? `${depth.reads.length + depth.gated.length} פעולות` : null}>
      <p className="text-[11px] text-gray-400">
        מה מותר להריץ כקריאה, מה מותר להריץ רק לאחר אישור אנושי, ומה מוצהר אך אינו זמין להרצה —
        לפי מרשם הכלים של התחום, לא לפי הצהרות.
      </p>

      {isLoading && <p className="text-sm text-gray-400 italic">טוען את מפת הפעולות של התחום...</p>}

      {!isLoading && isError && (
        <RetryNote
          message="לא ניתן לטעון את מפת הפעולות של התחום — הקריאות, הפעולות המחייבות אישור והפעולות שאינן זמינות אינן ידועות"
          onRetry={onRetry}
          retryLabel="נסה שוב לטעינת מפת הפעולות של התחום"
        />
      )}

      {!isLoading && !isError && !depth && (
        <p className="text-sm text-gray-400 italic">
          מפת הפעולות של התחום אינה זמינה כרגע — מצב הפעולות אינו ידוע.
        </p>
      )}

      {!isLoading && !isError && depth && (
        <>
          <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3 space-y-1.5">
            <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" aria-hidden="true" />
              קריאות נתמכות להרצה ({depth.reads.length})
            </h3>
            {depth.reads.length === 0 ? (
              <p className="text-xs text-gray-600">לא דווחו כלי קריאה להרצה לתחום זה.</p>
            ) : (
              <ul className="space-y-1.5">
                {depth.reads.map((tool) => (
                  <ActionToolRow
                    key={tool.name}
                    name={tool.name}
                    description={tool.description}
                    meta={Array.isArray(tool.allowed_tiers) && tool.allowed_tiers.length > 0 ? `tiers: ${tool.allowed_tiers.join(", ")}` : null}
                    chips={
                      <>
                        <span className="text-[11px] px-2 py-0.5 rounded-full border bg-blue-50 border-blue-200 text-blue-700 whitespace-nowrap">
                          קריאה בלבד
                        </span>
                        <ApprovalChips entry={tool} />
                      </>
                    }
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-1.5">
            <h3 className="text-sm font-semibold text-orange-800 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" aria-hidden="true" />
              פעולות המחייבות אישור ({depth.gated.length})
            </h3>
            {depth.gated.length === 0 ? (
              <p className="text-xs text-gray-600">
                לא רשומה פעולה משנה-מצב להרצה בתחום זה — פעולות שינוי מצב שאינן מופיעות כאן אינן
                זמינות דרך מרשם הכלים של התחום.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {depth.gated.map((tool) => (
                  <ActionToolRow
                    key={tool.name}
                    name={tool.name}
                    description={tool.description}
                    meta={Array.isArray(tool.allowed_tiers) && tool.allowed_tiers.length > 0 ? `tiers: ${tool.allowed_tiers.join(", ")}` : null}
                    chips={
                      <>
                        {!tool.strong_approval_required && !tool.approval_required && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 border-gray-200 text-gray-600 whitespace-nowrap">
                            לא סומן כדורש אישור
                          </span>
                        )}
                        <ApprovalChips entry={tool} />
                      </>
                    }
                  />
                ))}
              </ul>
            )}
            <p className="text-[11px] text-gray-500">
              הרצה מותנית באישור אנושי מפורש במסלול הצפייה הקיים — העובד אינו מאשר פעולות לעצמו
              ואינו מנמיך רמת סיכון.
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" aria-hidden="true" />
              פעולות שאינן זמינות ({unavailableCount})
            </h3>
            {unavailableCount === 0 && depth.blockedCount === 0 && !depth.beyondNote ? (
              <p className="text-xs text-gray-600">לא דווחו פעולות מוצהרות שאינן זמינות בתחום זה.</p>
            ) : (
              <ul className="space-y-1.5">
                {depth.unavailableConfigured.map((tool) => (
                  <ActionToolRow
                    key={`configured:${tool.name}`}
                    name={tool.name}
                    description={UNCONFIGURED_REASON[tool.reason] || null}
                    meta={bounded(tool.reason, 60) ? `reason: ${bounded(tool.reason, 60)}` : null}
                    chips={
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-red-50 border-red-200 text-red-700 whitespace-nowrap">
                        לא ניתנת להרצה
                      </span>
                    }
                  />
                ))}
                {depth.proposedWrites.map((contract) => (
                  <ActionToolRow
                    key={`proposed:${contract.tool_action_name}`}
                    name={contract.tool_action_name}
                    description="הצעת חוזה כתיבה מתועדת (PROPOSED_WRITE)."
                    meta={contract.permission_tier ? `tier: ${bounded(contract.permission_tier, 40)}` : null}
                    chips={
                      <>
                        <span className="text-[11px] px-2 py-0.5 rounded-full border bg-red-50 border-red-200 text-red-700 whitespace-nowrap">
                          לא ניתנת להרצה
                        </span>
                        <ApprovalChips entry={contract} />
                      </>
                    }
                  />
                ))}
              </ul>
            )}
            {depth.blockedCount > 0 && (
              <p className="text-xs text-gray-600">
                {depth.blockedCount} פעולות שסומנו כחסומות אינן מקודמות לביצוע לעולם.
              </p>
            )}
            {depth.beyondNote && <p className="text-[11px] text-gray-500">{depth.beyondNote}</p>}
          </div>
        </>
      )}
    </ShellCard>
  );
}

function RecentWorkSection({ domain, view, runs, runsLoading, runsError, onRetryRuns }) {
  const memberIds = view.members.map((m) => m.agent.id);
  const agentNameById = Object.fromEntries(view.members.map((m) => [m.agent.id, m.agent.name]));
  const { matched, shown } = domainRuns(runs, memberIds);

  return (
    <ShellCard title="עבודה אחרונה בתחום" count={matched > 0 ? `${shown} מתוך ${matched}` : null}>
      <p className="text-[11px] text-gray-400">
        מבוסס על רשומות הריצות המבוקרות — סיכומים חסומים באורך; אין כאן תוכן גלם, הנחיות או מטעני כלים.
        נסרקות {RECENT_RUNS_SCAN} הריצות האחרונות במערכת.
      </p>

      {runsLoading && <p className="text-sm text-gray-400 italic">טוען ריצות אחרונות...</p>}
      {!runsLoading && runsError && (
        <RetryNote
          message="לא ניתן לטעון את הריצות האחרונות"
          onRetry={onRetryRuns}
          retryLabel="נסה שוב לטעינת הריצות האחרונות"
        />
      )}

      {!runsLoading && !runsError && memberIds.length === 0 && (
        <p className="text-sm text-gray-400 italic">אין עובדים עם חיבור תחום מוגדר — לא ניתן לשייך ריצות לתחום.</p>
      )}
      {!runsLoading && !runsError && memberIds.length > 0 && matched === 0 && (
        <p className="text-sm text-gray-400 italic">
          לא נמצאו ריצות של עובדי התחום ב-{RECENT_RUNS_SCAN} הריצות האחרונות.
        </p>
      )}

      <ul className="space-y-2">
        {shown.map((run) => {
          const badge = RUN_STATUS_BADGE[run.status] || "bg-gray-100 text-gray-700 border-gray-200";
          const summary = bounded(run.result_summary, 200) || bounded(run.input_summary, 160) || "ריצה ללא סיכום זמין";
          return (
            <li key={run.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${badge}`}>
                  {runStatusLabel(run.status)}
                </span>
                <span className="text-xs text-gray-500">{agentNameById[run.agent_id] || bounded(run.agent_name, 60) || "עובד"}</span>
                <span className="text-[11px] text-gray-400 mr-auto whitespace-nowrap">{fmtDateTime(run.created_at)}</span>
              </div>
              <p className="text-sm text-gray-800">{summary}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                {run.ticket_id && (
                  <Link
                    to={`/tickets/${run.ticket_id}`}
                    className="text-blue-600 hover:underline"
                    aria-label={`פתח את הקריאה${run.ticket_number ? ` #${run.ticket_number}` : ""} שממנה הגיעה העבודה`}
                  >
                    {run.ticket_number ? `קריאה #${run.ticket_number}` : "קריאה מקושרת"}
                  </Link>
                )}
                <Link
                  to={`/agent-runs/${run.id}`}
                  className="text-blue-600 hover:underline"
                  aria-label={`פתח את הריצה${run.ticket_number ? ` של קריאה #${run.ticket_number}` : ""}`}
                >
                  צפה בריצה
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </ShellCard>
  );
}

function LimitsSection({ definition, canManage, connectorsLoading, connectorsError, onRetryConnectors }) {
  const statements = limitsStatements(definition);
  const capabilities = Array.isArray(definition?.capabilities) ? definition.capabilities : [];
  return (
    <ShellCard title="מגבלות ידועות">
      {!canManage && (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          קטלוג המחברים זמין למשתמשים עם הרשאת ניהול סוכנים — מגבלות המחבר אינן מוצגות למשתמש זה.
        </p>
      )}
      {canManage && connectorsLoading && <p className="text-sm text-gray-400 italic">טוען את קטלוג המחברים...</p>}
      {canManage && !connectorsLoading && connectorsError && (
        <RetryNote
          message="לא ניתן לטעון את קטלוג המחברים"
          onRetry={onRetryConnectors}
          retryLabel="נסה שוב לטעינת קטלוג המחברים"
        />
      )}
      {canManage && !connectorsLoading && !connectorsError && !definition && (
        <p className="text-sm text-gray-400 italic">לא נמצא תיאור מחבר מוגדר לתחום זה בקטלוג.</p>
      )}
      {canManage && !connectorsLoading && !connectorsError && definition && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            מחבר: <span className="font-medium text-gray-700">{bounded(definition.display_name, 80)}</span>
          </p>
          {statements.length > 0 ? (
            <ul className="list-disc pr-5 text-sm text-gray-700 space-y-0.5">
              {statements.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">לא דווחו מגבלות מובנות למחבר זה בקטלוג.</p>
          )}
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-600">יכולות מוצהרות במחבר</p>
            {capabilities.length === 0 ? (
              <p className="text-sm text-gray-400 italic">המחבר אינו מצהיר על יכולות בקטלוג.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {capabilities.map((token) => {
                  const label = capabilityLabel(token);
                  return label ? (
                    <span key={token} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      {label}
                    </span>
                  ) : (
                    <span
                      key={token}
                      dir="ltr"
                      title="יכולת שטרם תועדה בעברית"
                      className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-200 font-mono"
                    >
                      {bounded(token, 60)}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </ShellCard>
  );
}

function EscalationSection({ definition, readiness, view, runs, runsError, onRunsRetry, isAdmin }) {
  const memberIds = view.members.map((m) => m.agent.id);
  const agentNameById = Object.fromEntries(view.members.map((m) => [m.agent.id, m.agent.name]));
  const pending = runsError ? null : escalationRuns(runs, memberIds);
  const statements = escalationStatements(definition, readiness);
  return (
    <ShellCard title="הסלמה ובקרה אנושית" count={pending ? pending.length : null}>
      {runsError ? (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן להציג ריצות ממתינות — טעינת הריצות נכשלה.
          <button
            type="button"
            onClick={onRunsRetry}
            className="mr-2 underline"
            aria-label="נסה שוב לטעינת הריצות הממתינות"
          >
            נסה שוב
          </button>
        </p>
      ) : pending.length === 0 ? (
        <p className="text-sm text-gray-400 italic">אין ריצות הממתינות להחלטת טכנאי בתחום זה.</p>
      ) : (
        <div className="space-y-1.5">
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
            {pending.length === 1
              ? "ריצה אחת ממתינה להחלטת טכנאי (סקירה או אישור פעולה)."
              : `${pending.length} ריצות ממתינות להחלטת טכנאי (סקירה או אישור פעולה).`}
          </p>
          <ul className="space-y-1 text-sm">
            {pending.slice(0, 5).map((run) => (
              <li key={run.id} className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] px-1.5 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-200 whitespace-nowrap">
                  {runStatusLabel(run.status)}
                </span>
                <span className="text-gray-600">{agentNameById[run.agent_id] || "עובד"}</span>
                <Link
                  to={`/agent-runs/${run.id}`}
                  className="text-blue-600 hover:underline"
                  aria-label={`פתח את הריצה הממתינה${run.ticket_number ? ` של קריאה #${run.ticket_number}` : ""}`}
                >
                  {run.ticket_number ? `קריאה #${run.ticket_number}` : "צפה בריצה"}
                </Link>
                <span className="text-[11px] text-gray-400 mr-auto whitespace-nowrap">{fmtDateTime(run.created_at)}</span>
              </li>
            ))}
          </ul>
          {pending.length > 5 && (
            <p className="text-[11px] text-gray-500">
              מוצגות 5 הריצות הממתינות הראשונות מתוך {pending.length}.
            </p>
          )}
        </div>
      )}

      {statements.length > 0 && (
        <ul className="list-disc pr-5 text-xs text-gray-600 space-y-0.5">
          {statements.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <Link to="/agent-runs" className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50">לכל הריצות</Link>
        {isAdmin && (
          <Link to="/connections" className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50">ניהול חיבורים</Link>
        )}
      </div>
    </ShellCard>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SpecialistWorkspacePage() {
  const { domain: domainParam } = useParams();
  const domain = domainForParam(domainParam);
  const { can: canAgent } = useAgentPermissions();
  const { user } = useAuth();
  const canView = canAgent("agents_view");
  const canManage = canAgent("agents_manage");
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const now = Date.now();

  const agentsQuery = useQuery({
    queryKey: ["agents"],
    queryFn: agentsApi.list,
    enabled: canView && !!domain,
    staleTime: 30_000,
  });
  const agents = Array.isArray(agentsQuery.data) ? agentsQuery.data : [];

  const detailQueries = useQueries({
    queries: canView && domain
      ? agents.map((agent) => ({
          queryKey: ["agent", agent.id],
          queryFn: () => agentsApi.get(agent.id),
          staleTime: 30_000,
        }))
      : [],
  });
  const detailStates = detailQueries.map((q) => ({ data: q.data, isError: q.isError, isLoading: q.isLoading }));

  const runsQuery = useQuery({
    queryKey: ["agent-runs", "specialist-shell", RECENT_RUNS_SCAN],
    queryFn: () => agentRunsApi.listAll({ limit: RECENT_RUNS_SCAN }),
    enabled: canView && !!domain,
    staleTime: 30_000,
  });
  const runs = Array.isArray(runsQuery.data) ? runsQuery.data : [];

  const connectorsQuery = useQuery({
    queryKey: ["agentConnectorTypes"],
    queryFn: agentConnectionsApi.connectorTypes,
    enabled: canView && !!domain && canManage,
    staleTime: 5 * 60 * 1000,
  });

  const actionDepthQuery = useQuery({
    queryKey: ["specialist-domain-actions", domain?.backendKey],
    queryFn: () => api.get(`/api/agents/specialists/${domain.backendKey}`).then((res) => res.data),
    enabled: canView && !!domain && !!domain.backendKey,
    staleTime: 60 * 1000,
  });
  const connectorTypes = Array.isArray(connectorsQuery.data) ? connectorsQuery.data : [];
  const definition = domain ? connectorTypes.find((t) => t.connector_id === domain.connectorId) || null : null;

  if (!canView) {
    return (
      <div className="space-y-2" dir="rtl">
        <h1 className="text-xl font-bold text-gray-800">מרחב מומחים</h1>
        <p role="alert" className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          אין הרשאה לצפות במרחב המומחים — נדרשת הרשאת צפייה בסוכנים (agents_view).
        </p>
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="space-y-3" dir="rtl">
        <h1 className="text-xl font-bold text-gray-800">תחום מומחיות לא מוכר</h1>
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          התחום המבוקש "{bounded(String(domainParam ?? ""), 40) || "—"}" אינו מוגדר במרחב המומחים.
        </p>
        <DomainTabs activeDomain={{ key: null }} readinessByDomain={{}} />
      </div>
    );
  }

  const view = buildDomainView(agents, detailStates, domain);
  const readiness = domainReadiness(view, now);
  const readinessByDomain = {};
  for (const d of SPECIALIST_DOMAINS) {
    const dView = d.key === domain.key ? view : buildDomainView(agents, detailStates, d);
    readinessByDomain[d.key] = domainReadiness(dView, now);
  }

  const retryAgents = () => {
    agentsQuery.refetch();
    detailQueries.forEach((q) => q.refetch());
  };

  return (
    <div className="space-y-4" dir="rtl">
      <div className="space-y-2">
        <h1 className="text-xl font-bold text-gray-800">מרחב מומחים</h1>
        <p className="text-sm text-gray-500">
          מבט אחיד על תחומי המומחיות: מוכנות מבוססת-ראיות, עבודה אחרונה, מגבלות ידועות ומסלולי הסלמה.
        </p>
        <DomainTabs activeDomain={domain} readinessByDomain={readinessByDomain} />
      </div>

      <h2 className="text-base font-semibold text-gray-700">{domain.title}</h2>

      {agentsQuery.isError && (
        <RetryNote
          message="לא ניתן לטעון את רשימת העובדים"
          onRetry={() => agentsQuery.refetch()}
          retryLabel="נסה שוב לטעינת רשימת העובדים"
        />
      )}
      {agentsQuery.isLoading && <p className="text-sm text-gray-400 italic">טוען את רשימת העובדים...</p>}

      {!agentsQuery.isLoading && !agentsQuery.isError && (
        <>
          <ReadinessSection domain={domain} view={view} now={now} onRetry={retryAgents} />
          <ActionDepthSection
            payload={actionDepthQuery.data ?? null}
            isLoading={actionDepthQuery.isLoading}
            isError={actionDepthQuery.isError}
            onRetry={() => actionDepthQuery.refetch()}
          />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <RecentWorkSection
              domain={domain}
              view={view}
              runs={runs}
              runsLoading={runsQuery.isLoading}
              runsError={runsQuery.isError}
              onRetryRuns={() => runsQuery.refetch()}
            />
            <LimitsSection
              definition={definition}
              canManage={canManage}
              connectorsLoading={connectorsQuery.isLoading}
              connectorsError={connectorsQuery.isError}
              onRetryConnectors={() => connectorsQuery.refetch()}
            />
          </div>
          <EscalationSection
            definition={definition}
            readiness={readiness}
            view={view}
            runs={runs}
            runsError={runsQuery.isError}
            onRunsRetry={() => runsQuery.refetch()}
            isAdmin={isAdmin}
          />
        </>
      )}
    </div>
  );
}
